import Image from 'components/Common/Image'
import LineCommentsIcon from 'LuxKit/Icons/line/LineCommentsIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { rem } from 'polished'
import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import type { LocationFact } from 'tripPlanner/pages/trip/AITripConstants'
import { AITripTitles, locationFacts } from 'tripPlanner/pages/trip/AITripConstants'

const PROGRESS_BAR_HEIGHT = rem(16)

const Container = styled.div`
  display: grid;
  grid-template: 
    "progress-bar" ${PROGRESS_BAR_HEIGHT}
    "content" auto
    "." ${PROGRESS_BAR_HEIGHT} / minmax(0, 1fr);
  height: 100%;
  width: 100%;
`

const Content = styled(Sheet)`
  grid-area: content;
  place-self: center;
  max-width: ${rem(440)};
`

const FactText = styled(BodyText)`
  opacity: 1;
  transition: opacity 0.5s ease-in-out;
  
  &.fade-out {
    opacity: 0;
  }
`

const DetailText = styled(BodyText)`
  opacity: 1;
  transition: opacity 0.5s ease-in-out;
  
  &.fade-out {
    opacity: 0;
  }
`

const progressFillAnimation = keyframes`
  0% {
    width: 0%;
  }

  60% {
    width: 80%;
  }

  85% {
    width: 90%;
  }

  100% {
    width: 95%;
  }
`

const ProgressBar = styled.div`
  grid-area: progress-bar;
  height: ${PROGRESS_BAR_HEIGHT};
  background-color: ${props => props.theme.palette.neutral.default.six};
`

const ProgressBarFill = styled.div`
  height: 100%;
  background-color: ${props => props.theme.palette.highlight.primary.normalForeground};
  animation: ${progressFillAnimation} 100s cubic-bezier(0.4, 0, 0.2, 1) forwards;
`

const FACT_DURATION = 8000

interface Props {
  hotelCity?: string
}

function AITripGenerating(props: Props) {
  const { hotelCity } = props
  const intl = useIntl()

  const funFactsLabel = hotelCity
    ? intl.formatMessage(
        {
          id: 'd7omxjPfTsfZ11nh8DsE',
          defaultMessage: 'Fun facts while we create your {city} trip',
          description: 'Heading for fun facts section while AI trip is generating, with city name',
        },
        { city: hotelCity },
      )
    : intl.formatMessage({
        id: 'MlRU6bt-8gH3jFAODnPx',
        defaultMessage: 'Fun facts while we create your trip',
        description: 'Heading for fun facts section while AI trip is generating, without city name',
      })

  const availableFacts: Array<LocationFact> = useMemo(() => {
    if (!hotelCity) return locationFacts.Generic
    if (locationFacts[hotelCity]) return locationFacts[hotelCity]

    const partialMatch = Object.keys(locationFacts).find(key =>
      hotelCity.toLowerCase().includes(key.toLowerCase()) ||
      key.toLowerCase().includes(hotelCity.toLowerCase()),
    )

    if (partialMatch) return locationFacts[partialMatch]
    return locationFacts.Generic
  }, [hotelCity])

  const [factIndex, setFactIndex] = useState(0)
  const [isFading, setIsFading] = useState(false)
  const [titleIndex, setTitleIndex] = useState(0)
  const titles = AITripTitles(hotelCity || '')
  useEffect(() => {
    setFactIndex(0)
    setIsFading(false)
    setTitleIndex(0)
  }, [availableFacts])

  useEffect(() => {
    const interval = setInterval(() => {
      setIsFading(true)

      setTimeout(() => {
        setFactIndex((factIndex + 1) % availableFacts.length)
        setIsFading(false)
        setTitleIndex((titleIndex + 1) % titles.length)
      }, 500)
    }, FACT_DURATION)

    return () => clearInterval(interval)
  }, [factIndex, availableFacts.length, titleIndex, titles.length])

  const currentFact = availableFacts[factIndex]
  const currentTitle = titles[titleIndex]

  return <Container>
    <ProgressBar>
      <ProgressBarFill />
    </ProgressBar>
    <Content padding={16}>
      <Group direction="vertical" gap={20} tabletGap={40}>
        <div>
          <Image
            id="n7oimkvb3cnu7skx7u7"
            enhancedFormats={false}
            width={50}
            height={50}
          />
        </div>
        <Heading variant="heading1" className={isFading ? 'fade-out' : ''}>
          {currentTitle}
        </Heading>
        <Sheet background="neutral-seven" borderRadius="L" padding={24}>
          <Group direction="vertical" gap={8}>
            <BodyTextBlock variant="large" startIcon={<LineCommentsIcon />} colour="highlight-primary">
              {funFactsLabel}
            </BodyTextBlock>
            <div>
              <FactText variant="large" weight="bold" className={isFading ? 'fade-out' : ''}>
                {currentFact.text}
              </FactText>
              {currentFact.detail && <DetailText variant="large" className={isFading ? 'fade-out' : ''}>
                {currentFact.detail}
              </DetailText>}
            </div>
          </Group>
        </Sheet>
      </Group>
    </Content>
  </Container>
}

export default AITripGenerating
